import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import {
  ASSET_STATUSES,
  CONDITION_OPTIONS,
  type Asset,
  type CategoryWithSchema,
  type EditAssetFormData,
} from "./types";

interface BasicInfoFieldsProps {
  formData: EditAssetFormData;
  onFormChange: (updater: (prev: EditAssetFormData) => EditAssetFormData) => void;
  categories: CategoryWithSchema[];
  selectedCategoryId: string;
  onCategoryChange: (categoryId: string) => void;
}

export function BasicInfoFields({
  formData,
  onFormChange,
  categories,
  selectedCategoryId,
  onCategoryChange,
}: BasicInfoFieldsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="name">Asset Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => onFormChange(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter asset name"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category *</Label>
        <Select value={selectedCategoryId} onValueChange={onCategoryChange}>
          <SelectTrigger id="category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="manufacturer">Manufacturer</Label>
        <Input
          id="manufacturer"
          value={formData.manufacturer}
          onChange={(e) => onFormChange(prev => ({ ...prev, manufacturer: e.target.value }))}
          placeholder="Enter manufacturer"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="model_number">Model Number</Label>
        <Input
          id="model_number"
          value={formData.model_number}
          onChange={(e) => onFormChange(prev => ({ ...prev, model_number: e.target.value }))}
          placeholder="Enter model number"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="serial_number">Serial Number</Label>
        <Input
          id="serial_number"
          value={formData.serial_number}
          onChange={(e) => onFormChange(prev => ({ ...prev, serial_number: e.target.value }))}
          placeholder="Enter serial number"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="asset_code">Asset ID</Label>
        <Input
          id="asset_code"
          value={formData.asset_code}
          onChange={(e) => onFormChange(prev => ({ ...prev, asset_code: e.target.value }))}
          placeholder="Unique asset identifier"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="condition">Condition</Label>
        <Select
          value={formData.condition}
          onValueChange={(value) => onFormChange(prev => ({ ...prev, condition: value }))}
        >
          <SelectTrigger id="condition">
            <SelectValue placeholder="Select condition" />
          </SelectTrigger>
          <SelectContent>
            {CONDITION_OPTIONS.map(condition => (
              <SelectItem key={condition} value={condition}>{condition}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value) => onFormChange(prev => ({ ...prev, status: value as Asset["status"] }))}
        >
          <SelectTrigger id="status">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            {ASSET_STATUSES.map(status => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
